import * as THREE from "three";
import { VFX } from "../../../../../art/vfx.js";
import { AnimRig } from "../rig";
import { RigidRig } from "../rigidRig";
import { SkinFx } from "../skins/kit/fx";
import type { CompanionSpec } from "../skins/kit/types";
import { reducedVfx } from "../../ui/settings";
import { groundAt } from "../terrain";
import { HQ_YAW, hqNode, lib } from "./common";
import { BreathStream } from "./breathStream";
import { Gait, bankFor, breathLevel, breathTiming, ease, lateralPhase, pitchFor, swoopAt, wingRate, clearOf } from "./companionFlight";

/**
 * A fortress skin's companions (fortress v2, 2026-10-19; owner: "the dragon should be flying around the base"): the
 * creatures a premium fortress keeps in the air round it — the Dragonlord's drake flying laps of the walls and
 * swooping on the gate to breathe fire, the Moonstone Keep's moon owls circling its moon. Each is its own model
 * (`art/hq/fortress_*.glb`, rigid-rig clips `fly` / `glide` / an act / `roar`) flown on a closed path in the fortress's
 * frame: an ellipse round the keep (or round the strike crystal), pulled `ahead` toward the gate, with a slow vertical
 * wave. Looks only: the sim never sees them. Built per fortress view (views/towers.ts) and in the hub (hubScene.ts, no
 * VFX there).
 *
 * Flight feel (2026-10-20, owner: "the dragon needs work, as does its flame breath, and the flapping of the wings in
 * both it and the owls"; the maths in companionFlight.ts): the heading, bank and pitch come from the path's own
 * velocity — the act's dive included, so the drake noses down into its swoop — a coordinated-turn bank and both eased;
 * the wingbeat is set in beats a second and quickens on a climb (`gait`), flap and glide cross-fade with hysteresis,
 * and the breath clip blends in and out instead of cutting. The breath itself is views/breathStream.ts, timed to the
 * clip's jaw-open window (`breath.open`) and centred on the dive's low point in front of the gate.
 */
interface Flyer { root: THREE.Object3D; rig: AnimRig; phase: number; jaw: THREE.Object3D | null; head: THREE.Object3D | null; gait: Gait | null; bank: number; pitch: number; rate: number }

const _p = new THREE.Vector3();
const _q = new THREE.Vector3();
const _r = new THREE.Vector3();
const _v = new THREE.Vector3();
const _c = new THREE.Vector3();
const _t = new THREE.Vector3();
const _w = new THREE.Vector3();
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();

export class FortressCompanion {
  /** Seconds either side of now sampled for the path's velocity and turn rate (central differences). */
  static readonly Step = 0.05;
  /** How fast the bank and pitch follow the path (1/s), the turn's "gravity" (a stylised g in world units/s²), and the pitch's share of the path's slope and its limit (rad). */
  static readonly BankFollow = 4;
  static readonly PitchFollow = 5;
  static readonly BankG = 1.2;
  static readonly PitchGain = 0.85;
  static readonly PitchMax = 0.6;
  /** How fast the wingbeat's rate follows the climb (1/s), and the breath clip's blend in and out (s). */
  static readonly RateFollow = 3;
  static readonly ActBlend = 0.25;
  /** The loop phase passing the gate. */
  static readonly GatePhase = 0.25;
  /** How much the loop pulls in toward the keep at the bottom of the act's dive (share of the radius). */
  static readonly SwoopTighten = 0.25;

  private readonly flyers: Flyer[] = [];
  private t = Math.random() * 100;
  private actIn: number;
  /** Companion time the current act started (NaN: none), and whether its clip has started. */
  private actStart = NaN;
  private clipOn = false;
  /**
   * The loop phase the act's dive is centred on: the pass by the gate (0.25) until a camera is known, then the nearest
   * point to it where the drake crosses the view side-on (companionFlight.lateralPhase), so the breath reads as a long
   * diagonal across the screen.
   */
  private actU = FortressCompanion.GatePhase;
  private readonly breath: BreathStream | null;
  /** The fortress's yaw in the world: its gate (model +z after HQ_YAW) is the loop's u = 0.25. */
  private readonly yaw: number;
  /** The `fly` clip's length (s): the wingbeat's rate is set in beats a second (companionFlight.wingRate). */
  private flyClip = 1;

  private constructor(private readonly spec: CompanionSpec, private readonly fortress: THREE.Object3D, private readonly box: THREE.Box3,
                      private readonly tip: THREE.Object3D | null, private readonly parent: THREE.Object3D) {
    this.actIn = spec.act ? spec.act.every[0] : Infinity;
    this.yaw = new THREE.Euler().setFromQuaternion(fortress.getWorldQuaternion(new THREE.Quaternion()), "YXZ").y;
    this.breath = spec.act?.kind === "breath" && spec.breath ? new BreathStream(spec.breath) : null;
  }

  /** Where the lead flyer is round its loop, 0..1 (0.25: passing the gate). */
  private lap(): number { return this.flyers.length ? (this.t / this.spec.lap + this.flyers[0].phase) % 1 : 0; }

  /** The companions of a fortress model (`base_t2__dragonlord`), or null when its skin keeps none (or it is not loaded). */
  static of(fortress: THREE.Object3D, modelName: string, box: THREE.Box3, parent: THREE.Object3D): FortressCompanion | null {
    const spec = SkinFx.fortressKit(modelName)?.companion;
    const L = lib();
    if (!spec || !L || !L.hq(spec.model)) return null;
    const c = new FortressCompanion(spec, fortress, box.clone(), hqNode(fortress, modelName, "condgem0"), parent);
    c.flyClip = L.clipsOf(`hq:${spec.model}`).find((k) => k.name === spec.clips.fly)?.duration ?? c.flyClip;
    for (let i = 0; i < spec.count; i++) {
      const model = L.clone(`hq:${spec.model}`);
      model.rotation.y = HQ_YAW;
      const root = new THREE.Group();
      root.add(model);
      root.scale.setScalar(spec.scale);
      parent.add(root);
      const rig = new AnimRig(model, L.clipsOf(`hq:${spec.model}`));
      rig.playLoop(spec.clips.fly, 1, true);
      const gait = spec.gait && spec.clips.glide ? new Gait(spec.gait) : null;
      c.flyers.push({ root, rig, phase: i / spec.count, jaw: hqNode(model, spec.model, "jaw"), head: hqNode(model, spec.model, "head"), gait, bank: 0, pitch: 0, rate: 1 });
    }
    c.update(0, null);
    return c;
  }

  /** Where flyer `i` is at companion time `t` (world): its loop round the keep or the crystal, in the fortress's own frame. */
  private at(i: number, t: number, out: THREE.Vector3): THREE.Vector3 {
    const s = this.spec, f = this.flyers[i];
    const a = ((t / s.lap + f.phase) % 1) * Math.PI * 2;
    const swoop = this.swoopAt(i, t);
    const centre = s.around === "tip" && this.tip ? this.tip.getWorldPosition(_c) : this.box.getCenter(_c).setY(this.box.max.y);
    const r = s.radius * (1 - FortressCompanion.SwoopTighten * swoop);
    out.set(Math.cos(a) * r, s.height + s.bob * Math.sin(a * 2 + i) - swoop * s.dip, Math.sin(a) * r * s.squash + (s.ahead ?? 0));
    out.applyAxisAngle(THREE.Object3D.DEFAULT_UP, this.yaw);   // the loop turns with the fortress: u = 0.25 is the gate
    out.add(centre);
    out.y = clearOf(out.y, this.box.max.y, s.clear);
    return out;
  }

  /** 0..1: how deep into its act's dive flyer `i` is at time `t` — only the first flyer acts. */
  private swoopAt(i: number, t: number): number {
    if (i !== 0 || !this.spec.act || Number.isNaN(this.actStart)) return 0;
    return swoopAt((t - this.actStart) / this.spec.act.seconds);
  }

  /** The fortress's reveal beat for a companion skin (views/towerMotion.ts reveal): the lead flyer roars now and acts at the gate next. */
  reveal(fx: VFX | null): void {
    const f = this.flyers[0];
    if (!f) return;
    if (this.spec.clips.roar) f.rig.playOnce(this.spec.clips.roar, false, null, { solo: true, blend: FortressCompanion.ActBlend });
    this.actIn = 0;                                           // and breathes on its next pass of the gate (not over the roar)
    if (fx) fx.flash(f.root.getWorldPosition(_p), 1.0, this.spec.glow ?? [1, 0.6, 0.25]);
  }

  /** Review tooling (tools/video/companion_closeup.mjs): jump the loop to `lead` s before the next act's start at the gate. */
  debugActSoon(lead: number): void {
    const s = this.spec;
    if (!s.act || !this.flyers.length) return;
    const due = this.actU - s.act.seconds / 2 / s.lap;             // where the lead flyer is when an act starts
    const behind = (((this.lap() - due) % 1) + 1) % 1;
    this.t += (1 - behind) * s.lap - lead;
    this.actIn = 0; this.actStart = NaN; this.clipOn = false;
  }

  private startAct(): void {
    if (!this.spec.act) return;
    this.actStart = this.t;
    this.clipOn = false;
    this.actIn = this.spec.act.every[0] + Math.random() * (this.spec.act.every[1] - this.spec.act.every[0]);
  }

  update(dt: number, fx: VFX | null): void {
    this.t += dt;
    this.stepAct(dt);
    for (let i = 0; i < this.flyers.length; i++) this.fly(i, dt);
    if (fx && !reducedVfx()) this.effects(dt, fx);
  }

  /** The act's clock: one is due every few seconds and starts as the lead flyer comes round to the gate; its clip starts on cue. */
  private stepAct(dt: number): void {
    const a = this.spec.act, f = this.flyers[0];
    if (!a || !f) return;
    const into = this.t - this.actStart;
    if (into >= a.seconds) this.actStart = NaN;
    if (Number.isNaN(this.actStart)) {
      const u = (this.lap() + a.seconds / 2 / this.spec.lap) % 1;   // the dive is centred on actU
      const window = (2 * Math.max(dt, FortressCompanion.Step)) / this.spec.lap;
      const past = (((u - this.actU) % 1) + 1) % 1;
      if ((this.actIn -= dt) <= 0 && past < window) this.startAct();
    } else if (!this.clipOn && this.spec.clips.act) {
      const cue = this.spec.breath ? breathTiming(this.spec.breath, a.seconds) : 0;
      if (into >= cue) {
        this.clipOn = true;
        f.rig.playOnce(this.spec.clips.act, false, null, { solo: true, blend: FortressCompanion.ActBlend, timeScale: this.spec.breath?.rate ?? 1 });
      }
    }
  }

  /** Flies flyer `i` one frame: position, heading, eased bank and pitch, and its wings (rate and flap ↔ glide). */
  private fly(i: number, dt: number): void {
    const s = this.spec, f = this.flyers[i], h = FortressCompanion.Step;
    this.at(i, this.t - h, _p);
    this.at(i, this.t + h, _q);
    this.at(i, this.t, _r);
    _v.subVectors(_q, _p).divideScalar(2 * h);
    const vh = Math.hypot(_v.x, _v.z);
    const yaw = Math.atan2(_v.x, _v.z);
    let turn = Math.atan2(_q.x - _r.x, _q.z - _r.z) - Math.atan2(_r.x - _p.x, _r.z - _p.z);
    if (turn > Math.PI) turn -= Math.PI * 2; else if (turn < -Math.PI) turn += Math.PI * 2;
    const bank = -bankFor(vh, turn / h, FortressCompanion.BankG, s.bank);
    f.bank = dt > 0 ? ease(f.bank, bank, FortressCompanion.BankFollow, dt) : bank;
    const pitch = pitchFor(_v.y, vh, FortressCompanion.PitchGain, FortressCompanion.PitchMax);
    f.pitch = dt > 0 ? ease(f.pitch, pitch, FortressCompanion.PitchFollow, dt) : pitch;
    f.root.position.copy(_r);
    f.root.rotation.set(f.pitch, yaw, f.bank, "YXZ");
    this.wings(f, dt, _v.y);
    f.rig.update(dt);
  }

  /** The wings: no change while a one-shot plays; otherwise flap (at a rate set by the climb) or glide, cross-faded. */
  private wings(f: Flyer, dt: number, climb: number): void {
    const s = this.spec, g = s.gait;
    if (f.rig.busy || !g) {
      if (!g && s.clips.glide) f.rig.playLoop(climb < -0.15 ? s.clips.glide : s.clips.fly, 1);   // a kit without a gait: the old rule
      return;
    }
    f.gait?.step(dt, climb);
    if (f.gait?.gliding && s.clips.glide) { f.rig.playLoop(s.clips.glide, 1, false, g.fade); return; }
    const rate = wingRate(g, this.flyClip, climb);
    f.rate = dt > 0 ? ease(f.rate, rate, FortressCompanion.RateFollow, dt) : rate;
    f.rig.playLoop(s.clips.fly, f.rate, false, g.fade);
  }

  /** The lead flyer's breath of fire (the drake), and the flyers' sparkle trail. */
  private effects(dt: number, fx: VFX): void {
    const s = this.spec, f = this.flyers[0];
    if (!f) return;
    if (s.trail) for (const g of this.flyers) if (Math.random() < dt * s.trail.perSecond) fx.sparkles(g.root.getWorldPosition(_p), 1, { color: s.trail.colour, radius: 0.08, rise: -0.1, life: s.trail.life, size: 0.08 });
    const b = s.breath;
    if (!this.breath || !b) return;
    if (Number.isNaN(this.actStart)) {
      fx.camera.getWorldDirection(_q);
      this.actU = lateralPhase(_q.x, _q.z, this.yaw, s.squash, FortressCompanion.GatePhase);
    }
    const level = this.clipOn && f.rig.busy ? breathLevel(b, f.rig.oneShotTime) : 0;
    if (level <= 0) { this.breath.frame(dt, fx, 0, _p, _p, _p, _v, 0); return; }
    // the mouth: between the lower lip (jaw frame) and the upper lip (head frame), so it rides the jaw and the head
    const mouth = this.mouth(f, b, _p);
    // the snout's direction (the model faces −z in the head's frame), or the heading for a model without a head node
    if (f.head) f.head.localToWorld(_q.set(0, 0, -1)).sub(f.head.getWorldPosition(_r)).normalize();
    else f.root.getWorldDirection(_q);
    _x.copy(_q);
    f.root.getWorldDirection(_q);
    _q.setY(0).normalize();
    f.root.getWorldPosition(_t).addScaledVector(_q, b.aim);
    _t.y = groundAt(_t.x, _t.z);
    // the drake's own velocity (the stream keeps some of it): the path's derivative, as in fly()
    this.at(0, this.t + FortressCompanion.Step, _r);
    this.at(0, this.t - FortressCompanion.Step, _w);
    _v.subVectors(_r, _w).divideScalar(2 * FortressCompanion.Step);
    this.breath.frame(dt, fx, level, mouth, _x, _t, _v, _t.y);
  }

  /** The lead flyer's mouth (world) from the kit's lip points; the jaw node itself when the model lacks a head. */
  private mouth(f: Flyer, b: NonNullable<CompanionSpec["breath"]>, out: THREE.Vector3): THREE.Vector3 {
    if (!f.jaw || !f.head) return (f.jaw ?? f.root).getWorldPosition(out);
    f.jaw.localToWorld(out.set(b.lips.jaw[0], b.lips.jaw[1], b.lips.jaw[2]));
    f.head.localToWorld(_y.set(b.lips.head[0], b.lips.head[1], b.lips.head[2]));
    return out.add(_y).multiplyScalar(0.5);
  }

  /** Review tooling: the lead flyer's mouth (world) now, to check the fire leaves it (null without a breath). */
  debugMouth(): THREE.Vector3 | null {
    const f = this.flyers[0], b = this.spec.breath;
    return f && b ? this.mouth(f, b, new THREE.Vector3()) : null;
  }

  dispose(): void {
    for (const f of this.flyers) { this.parent.remove(f.root); RigidRig.dispose(f.root); }
    this.flyers.length = 0;
  }
}
